import { useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import moment from "moment"
import toast from "react-hot-toast"

import { Pagination } from "components"
import Table from "components/table"
import Modal from "components/modal"
import Input from "components/forms/input"
import Label from "components/forms/label"
import SelectDescription from "components/forms/select-description"
import { useAuth } from "contexts/auth-context"

import useBranchRepositories from "repositories/branch"
import useStockTransferRecordRepositories from "repositories/stock-transfer-record"

const STATUS_OPTIONS = [
  { id: "ALL", name: "Semua Status" },
  { id: "DRAFT", name: "Draft" },
  { id: "POSTED", name: "Posted" },
  { id: "CANCELLED", name: "Cancelled" },
  { id: "RECEIVED", name: "Received" },
]

export default function List() {
  const { can, isSuper, currentBranch } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchInput = useRef(null)
  const [searchText, setSearchText] = useState(searchParams.get("search") || "")
  const [detailTransfer, setDetailTransfer] = useState(null)

  const isBranchLocked = !isSuper() && !!currentBranch?.id
  const rawOutletId = searchParams.get("outlet_id") || "ALL"
  const outletId = isBranchLocked ? String(currentBranch.id) : rawOutletId
  const status = searchParams.get("status") || "ALL"
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""
  const page = searchParams.get("page") || "1"

  const { data: branchSelection, isLoading: isLoadingBranches } = useBranchRepositories({ paginate: false })
  const branchOptions = useMemo(() => {
    const branches = Array.isArray(branchSelection) ? branchSelection : []
    if (isBranchLocked) return branches
    return [{ id: "ALL", name: "Semua Outlet" }, ...branches]
  }, [branchSelection, isBranchLocked])

  const { data, isLoading, mutate } = useStockTransferRecordRepositories({
    search: searchParams.get("search") || undefined,
    outlet_id: isBranchLocked ? outletId : (outletId === "ALL" ? undefined : outletId),
    status: status === "ALL" ? undefined : status,
    with_items: 1,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
  })

  const rows = useMemo(() => data?.data ?? [], [data])
  const canCreateTransfer = can("stock-transfer.create") || can("stock-movement.view")

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }

    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
        delete merged[key]
      }
    })

    setSearchParams(merged)
  }

  const updateSearch = (value) => {
    setSearchText(value)
    mergeParams({ search: value || null, page: 1 })
  }

  const headers = [
    { isAction: false, content: "Delivery Order No" },
    { isAction: false, content: "Date" },
    { isAction: false, content: "Items" },
    { isAction: false, content: "From Outlet" },
    { isAction: false, content: "To Outlet" },
    { isAction: false, content: "Status" },
    { isAction: false, content: "Qty" },
    { isAction: false, content: "Created By" },
    { isAction: true, content: "Action" },
  ]

  const postTransfer = async (row) => {
    const confirmed = window.confirm(`Post stock transfer ${row.delivery_order_no}?`)
    if (!confirmed) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-transfers/${row.id}/post`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      )
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal post stock transfer")
      }

      toast.success(json?.message || "Stock transfer berhasil diposting")
      await mutate()
    } catch (error) {
      toast.error(error?.message || "Gagal post stock transfer")
    }
  }

  const cancelTransfer = async (row) => {
    const confirmed = window.confirm(`Batalkan draft stock transfer ${row.delivery_order_no}?`)
    if (!confirmed) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-transfers/${row.id}/cancel`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      )
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal cancel stock transfer")
      }

      toast.success(json?.message || "Draft stock transfer berhasil dibatalkan")
      await mutate()
    } catch (error) {
      toast.error(error?.message || "Gagal cancel stock transfer")
    }
  }

  const columns = [
    {
      type: "default",
      bold: true,
      content: (v) => (
        <div className="flex flex-col gap-1">
          <span>{v.delivery_order_no || "-"}</span>
          {Number(v._movement_count ?? 0) > 0 && (
            <span className="text-[11px] font-medium text-blue-600">
              {v._movement_count} stock movement rows
            </span>
          )}
        </div>
      ),
    },
    { type: "default", content: (v) => (v.display_date || v.transfer_date || v.created_at) ? moment(v.display_date || v.transfer_date || v.created_at).format("YYYY-MM-DD HH:mm") : "-" },
    {
      type: "default",
      content: (v) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-700">{v.total_items || 0} item</span>
          <button
            type="button"
            onClick={() => setDetailTransfer(v)}
            className="px-2 py-1 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md"
          >
            Detail
          </button>
        </div>
      ),
    },
    { type: "default", content: (v) => v.source_outlet_name || "-" },
    { type: "default", content: (v) => v.dest_outlet_name || "-" },
    {
      type: "default",
      content: (v) => (
        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
          v.status === "DRAFT"
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : v.status === "POSTED"
            ? "bg-blue-50 text-blue-700 border-blue-200"
            : v.status === "CANCELLED"
            ? "bg-red-50 text-red-700 border-red-200"
            : "bg-green-50 text-green-700 border-green-200"
        }`}>
          {v.status}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <div className="text-right">
          <div className="font-semibold text-neutral-700">{v.total_qty_requested ?? v.qty ?? 0}</div>
          {Number(v.total_qty_received ?? 0) > 0 && (
            <div className="text-[11px] text-neutral-500">received {v.total_qty_received}</div>
          )}
        </div>
      ),
    },
    { type: "default", content: (v) => v.created_by || "-" },
    {
      type: "action",
      content: (v) => (
        v.status === "DRAFT" ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => postTransfer(v)}
              className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg"
            >
              Post
            </button>
            <button
              type="button"
              onClick={() => cancelTransfer(v)}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
            >
              Cancel
            </button>
          </div>
        ) : (
          <span className="text-xs text-neutral-400">-</span>
        )
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Stock Transfer</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Daftar transfer stock antar outlet berdasarkan nomor Delivery Order.
            </p>
          </div>

          {canCreateTransfer && (
            <Link
              to="/stock-transfer/create"
              className="inline-flex items-center px-4 py-3 text-sm font-medium text-white bg-neutral-900 rounded-xl"
            >
              Add Stock Transfer
            </Link>
          )}
        </div>

          <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-5">
          <div>
            <Label value="Outlet" />
            <SelectDescription
              selection={branchOptions}
              isLoading={isLoadingBranches}
              value={outletId}
              onChange={(value) => mergeParams({ outlet_id: value || "ALL", page: 1 })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={(v) => v.code || v.id}
              placeholder="Select Outlet..."
            />
          </div>
          <div>
            <Label value="Status" />
            <SelectDescription
              selection={STATUS_OPTIONS}
              value={status}
              onChange={(value) => mergeParams({ status: value || "ALL", page: 1 })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={() => ""}
              placeholder="Select Status..."
            />
          </div>
          <div>
            <Label value="Date From" />
            <Input type="date" value={dateFrom} onChange={(e) => mergeParams({ date_from: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Date To" />
            <Input type="date" value={dateTo} onChange={(e) => mergeParams({ date_to: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Search Delivery Order" />
            <Input
              ref={searchInput}
              type="text"
              value={searchText}
              placeholder="Delivery order no..."
              onChange={(e) => updateSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <Table
          isLoading={isLoading}
          data={rows}
          headers={headers}
          columns={columns}
          key={(v) => `${v.delivery_order_no}-${v.product_unit_id}`}
          isEmpty={!rows.length}
        />
        <Pagination links={data?.links} from={data?.from} to={data?.to} total={data?.total} />
      </div>

      <Modal
        isOpen={!!detailTransfer}
        onClose={() => setDetailTransfer(null)}
        title={`Detail Transfer ${detailTransfer?.delivery_order_no || ""}`}
        size="3xl"
        content={(
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">From Outlet</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.source_outlet_name || "-"}</div>
              </div>
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">Receiving Outlet</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.dest_outlet_name || "-"}</div>
              </div>
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">Status</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.status || "-"}</div>
              </div>
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">Total Qty</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.total_qty_requested ?? detailTransfer?.qty ?? 0}</div>
              </div>
            </div>

            <div className="overflow-hidden border rounded-xl border-neutral-200">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Qty Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {!(detailTransfer?.items || []).length && (
                    <tr>
                      <td colSpan={2} className="px-3 py-4 text-center text-neutral-500">
                        Detail item belum tersedia.
                      </td>
                    </tr>
                  )}
                  {(detailTransfer?.items || []).map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="px-3 py-3">{item.product_unit_name || "-"}</td>
                      <td className="px-3 py-3 text-right">{item.qty_requested ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      />
    </div>
  )
}
